import {
  Dialog,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import React from "react";

import { deleteLoanApplication as deleteLoanApplicationAPI } from "api/loanApplications";
import { loanApplicationsKeys } from "api/loanApplications/queries";
import { LoanApplicationsType } from "api/loanApplications/types";
import UltimateButton from "components/UltimateButton";

interface DeleteLoanApplicationModalProps {
  isOpen: boolean;
  handleClose: () => void;
  loanApplication: LoanApplicationsType | undefined;
}

const DeleteLoanApplicationModal = ({
  isOpen,
  handleClose,
  loanApplication,
}: DeleteLoanApplicationModalProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteLoanApplication, status } = useMutation(
    async (id: string) => {
      const res = await deleteLoanApplicationAPI(id);
      await queryClient.invalidateQueries(loanApplicationsKeys.list());
      return res.data;
    },
  );

  const handleDelete = async () => {
    if (!loanApplication?._id) return;
    await deleteLoanApplication(loanApplication._id, {
      onSuccess: () => handleClose(),
    });
  };

  const hasLender = Boolean(loanApplication?.lender);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          py: 2,
          pb: 3,
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          spacing={0.5}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            p={1.5}
            sx={{
              bgcolor: (t) =>
                (t.palette as any).brandDanger?.secondary ?? "#FEE9E8",
              borderRadius: "50%",
            }}
          >
            <Trash2
              size={32}
              color={(theme.palette as any).brandDanger?.primary ?? "#EC1F3D"}
            />
          </Stack>
          <Typography variant="headingMD" fontWeight={600}>
            Delete loan application
          </Typography>
          <Typography
            variant="textSM"
            textAlign="center"
            sx={{
              color: (t) =>
                (t.palette as any).brandText?.secondary ?? "#A2A8B6",
            }}
          >
            Are you sure you want to delete{" "}
            {hasLender ? (
              <>
                the loan application for lender{" "}
                <span
                  style={{
                    color:
                      (theme.palette as any).brandText?.primary ?? "#111726",
                  }}
                >
                  {loanApplication?.lender}
                </span>
              </>
            ) : (
              <span
                style={{
                  color: (theme.palette as any).brandText?.primary ?? "#111726",
                }}
              >
                this loan application
              </span>
            )}
            ? This action cannot be undone.
          </Typography>
        </Stack>
      </DialogTitle>

      <Stack width="100%" alignItems="center" justifyContent="center">
        <UltimateButton
          text="Delete"
          color="brandDanger"
          Icon={
            Trash2 as React.ComponentType<{
              size?: number;
              className?: string;
              style?: React.CSSProperties;
            }>
          }
          onClick={handleDelete}
          loading={status === "loading"}
          disabled={!loanApplication?._id}
          sx={{ py: 1 }}
        />
      </Stack>
    </Dialog>
  );
};

export default DeleteLoanApplicationModal;
